import './ProjectListFilterGroup.scss';
import { FieldPath } from '@form-ts/core';
import { useFormWatch } from '@form-ts/react';
import clsx from 'clsx';
import { pipe } from 'fp-ts/function';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button } from 'src/modules/common/components/Button';
import { Checkbox } from 'src/modules/common/components/Checkbox';
import { ResetIcon } from 'src/modules/common/icons/ResetIcon';
import { FormError } from 'src/modules/form/types/FormError';
import { ProjectListFilter } from 'src/modules/project-list/types/ProjectListFilter';

type Props<T> = {
  readonly field: FieldPath<ProjectListFilter, FormError, ReadonlySet<T>>;
  readonly options: ReadonlyArray<T>;
  readonly vertical?: boolean;
  readonly previewOptionsLength?: number;
  readonly renderOption: (option: T) => React.ReactElement | string;
  readonly onApply: () => void;
};

// eslint-disable-next-line react/function-component-definition
export function ProjectListFilterGroup<T>({
  field,
  options,
  vertical,
  previewOptionsLength = 3,
  renderOption,
  onApply,
}: Props<T>): React.ReactElement {
  const value = useFormWatch(field.form, field.value.get);

  const [isGroupOpened, setGroupOpened] = useState(false);

  const someSelected = useMemo(() => options.some((it) => value.has(it)), [options, value]);

  const previewOptions = options.slice(0, previewOptionsLength);

  const list = isGroupOpened ? options : previewOptions;

  const resetAll = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();

    const newValue = new Set<T>();

    field.form.change(pipe(
      field.form.currentState,
      field.value.set(newValue),
    ));

    onApply();
  }, [field, onApply]);

  const toggleOption = useCallback((option: T) => {
    const oldValue = field.value.get(field.form.currentState);
    const newValue = toggleSetValue(oldValue, option);

    field.form.change(pipe(
      field.form.currentState,
      field.value.set(newValue),
    ));

    onApply();
  }, [field, onApply]);

  return (
    <>
      <div className="bp-project-list-filter-group">
        <div className={clsx({
          'bp-project-list-filter-group__list': true,
          'bp-project-list-filter-group__list--vertical': vertical,
        })}
        >
          {list.map((option) => (
            <div
              className="bp-project-list-filter-group__label"
              key={String(option)}
            >
              <Checkbox
                id={`bp-project-list-filter-group__list-${field.form.name}-${field.path}-${String(option)}`}
                label={renderOption(option)}
                checked={value.has(option)}
                onChange={() => toggleOption(option)}
              />
            </div>
          ))}
        </div>
      </div>

      {(options.length > previewOptionsLength || someSelected) && (
        <div className="bp-project-list-filter-group__buttons">
          {options.length > previewOptionsLength ? (
            <button
              type="button"
              className="bp-project-list-filter-group__button"
              onClick={() => setGroupOpened((prev) => !prev)}
            >
              {isGroupOpened ? (
                <FormattedMessage id="projectList/filterDropdown/viewLess"/>
              ) : (
                <FormattedMessage id="projectList/filterDropdown/viewMore"/>
              )}
            </button>
          ) : null}

          {someSelected ? (
            <button type="button" className="bp-project-list-filter-group__button" onClick={resetAll}>
              <FormattedMessage id="projectList/filterDropdown/clear"/>
            </button>
          ) : null}
        </div>
      )}
    </>
  );
}

function toggleSetValue<T>(value: ReadonlySet<T>, option: T): Set<T> {
  const newValue = new Set(value);
  if (newValue.has(option)) {
    newValue.delete(option);
  } else {
    newValue.add(option);
  }

  return newValue;
}
